/**
 * Sun-outage monitor, the monitor half of loader-sunoutage.js plus the badge
 * rule from loader-app.js 198-223: every 60 s predict outages over the next
 * 6 h for the first 50 antennas at a 120 s coarse step (predictSunOutages from
 * the analysis module), keep the list, and raise the badge for the first
 * outage whose peak is within 15 min or that is in progress. The monitor runs
 * on real time, as the original did.
 */
import { predictSunOutages } from '../analysis';
import type { SunOutageEvent } from '../analysis/types';
import type { SunOutageBadge } from '../store';
import type { Antenna, Mission } from '../types';

export const MONITOR_INTERVAL_MS = 60_000;
export const LOOK_AHEAD_HOURS = 6;
export const ANTENNA_LIMIT = 50;
export const COARSE_STEP_SEC = 120;
export const BADGE_WINDOW_MIN = 15;

/** Outages whose peak is less than 15 min ahead, including those still in progress (peak within durationSec ago). */
export function imminentOutages(predictions: readonly SunOutageEvent[], nowMs: number): SunOutageEvent[] {
  return predictions.filter((p) => {
    const dtMin = (p.peakTimeMs - nowMs) / 60_000;
    return dtMin > -p.durationSec / 60 && dtMin < BADGE_WINDOW_MIN;
  });
}

/** The badge for the first imminent outage, null when there is none. */
export function sunOutageBadge(predictions: readonly SunOutageEvent[], nowMs: number): SunOutageBadge | null {
  const imminent = imminentOutages(predictions, nowMs);
  if (imminent.length === 0) return null;
  const p = imminent[0];
  return {
    antennaId: p.antId,
    missionId: p.satId,
    peakMs: p.peakTimeMs,
    durationSec: p.durationSec,
    separationDeg: p.peakSeparationDeg,
  };
}

/** 'IN SUN OUTAGE NOW' once the peak has passed, else 'Sun outage in N min'. */
export function badgeLabel(badge: SunOutageBadge, nowMs: number): string {
  const dtMin = Math.round((badge.peakMs - nowMs) / 60_000);
  return dtMin <= 0 ? 'IN SUN OUTAGE NOW' : `Sun outage in ${dtMin} min`;
}

/** One scan: the first 50 antennas against every TLE mission, now to now + 6 h, 120 s coarse step. */
export function runSunOutageScan(antennas: readonly Antenna[], missions: readonly Mission[], nowMs: number): SunOutageEvent[] {
  if (antennas.length === 0 || missions.length === 0) return [];
  return predictSunOutages({
    antennas: antennas.slice(0, ANTENNA_LIMIT),
    missions: missions.filter((m) => m.orbit && m.orbit.tle),
    startMs: nowMs,
    endMs: nowMs + LOOK_AHEAD_HOURS * 3_600_000,
    coarseStepSec: COARSE_STEP_SEC,
  });
}

/**
 * Scan now and every 60 s until stopped, handing the predictions and the badge
 * to apply. A scan that throws is logged and the monitor keeps running.
 */
export function startSunOutageMonitor(
  inputs: () => { antennas: readonly Antenna[]; missions: readonly Mission[] },
  apply: (events: SunOutageEvent[], badge: SunOutageBadge | null, nowMs: number) => void,
  nowMs: () => number = Date.now,
): () => void {
  const run = () => {
    const now = nowMs();
    const { antennas, missions } = inputs();
    try {
      const events = runSunOutageScan(antennas, missions, now);
      apply(events, sunOutageBadge(events, now), now);
    } catch (err) {
      console.error('[sunOutage] scan failed:', err);
    }
  };
  run();
  const id = setInterval(run, MONITOR_INTERVAL_MS);
  return () => clearInterval(id);
}
